import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const lerumPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-lerum",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Lerum – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Lerum. Vi hämtar möbler, vitvaror och skräp i Lerum, Floda, Gråbo, Stenkullen, Sjövik och Tollered. Fast pris, ofta samma dag.",
      h1: "Bortforsling Lerum",
    },

    hero: {
      heading: "Bortforsling i Lerum",
      intro:
        "Boka bortforsling i Lerum – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från villorna i Lerum centrum och vid Aspen till Floda, Gråbo, Stenkullen och Sjövik. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Lerum – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Lerum",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Lerum och körs till Hultets återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Floda, Lerum",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Lerum centrum",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Gråbo, Lerum",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Stenkullen, Lerum",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Sjövik, Lerum",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Lerum – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Lerum är en grön kommun öster om Göteborg, känd för sina sjöar Aspen och Sävelången, Säveån som rinner genom kommunen och en av Sveriges högsta andelar villaägande. Orterna Lerum, Floda och Gråbo dominerar, tillsammans med mindre samhällen som Stenkullen, Sjövik, Björboholm och Tollered. Bebyggelsen är till största delen villor och radhus – från de gamla sommarvillorna vid sjöarna till moderna villaområden – med inslag av lägenheter nära stationerna. Den blandningen gör att bortforsling i Lerum ofta handlar om att tömma garage, källare, förråd och vindar i villor, men ibland också om trapphus och hiss i lägenhetshus. Med Ungdomskraft slipper du allt krångel. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din villa, lägenhet eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. För Lerum är Hultets återvinningscentral kommunens egen station, där grovavfall, elavfall och farligt avfall tas emot gratis för dig som bor i kommunen. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E20 går rakt genom Lerum och pendeltåget mellan Göteborg och Alingsås stannar vid Aspen, Aspedalen, Lerum, Stenkullen och Floda tar vi oss snabbt mellan orterna, och vi når inlandsorten Gråbo smidigt via Gråbovägen. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både villatomterna kring Aspen och de äldre sommarvillorna vid Sävelången – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Lerum",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Eftersom de flesta uppdrag i Lerum är villahämtningar är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas, och om uppfarten är grusad eller brant ner mot sjön. Bor du i lägenhet i Lerum centrum eller Floda, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget, förrådet eller vid sjöboden. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Lerumsbo på Hultets återvinningscentral.",
      additionalSections: [
        {
          heading: "Orter och områden vi täcker i Lerum",
          content:
            "Vi utför bortforsling i hela Lerums kommun – från de centrala orterna till villaområdena vid sjöarna och inlandets samhällen. Oavsett om du bor i en villa vid Aspen, ett radhus i Floda, en lägenhet i Lerum centrum eller ett hus i Gråbo kan vi vara på plats, ofta redan samma dag.\n\nI Lerum – med centrum, Aspedalen, Hulan, Hallsås och Dergården – har vi stor vana vid både villahämtningar och lägenhetshämtningar nära pendeltågsstationen. I Floda och Stenkullen möter vi villabebyggelse längs Säveån och de kulturhistoriska miljöerna kring Nääs slott och Tollered.\n\nVi är minst lika snabba i kommunens övriga delar: Gråbo med Hjällsnäs och Stamsjö i norr, samt Sjövik och Björboholm vid Sävelången. Tack vare närheten till E20, pendeltåget och Gråbovägen planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon ort.",
        },
        {
          heading: "Återvinning och miljöansvar i Lerum",
          content:
            "En viktig del av vår bortforsling i Lerum är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. Lerum har en egen station, och avfallshämtningen i kommunen sköts av Renova:\n\nHultets återvinningscentral – Lerums egen återvinningscentral, där sorterat grovavfall, elavfall, trädgårdsavfall och farligt avfall tas emot, liksom föremål för återbruk. Eftersom Lerum samarbetar med övriga kommuner i Göteborgsregionen kan vi vid behov även lämna avfall på närliggande stationer i Partille, Alingsås eller Göteborg. Vi väljer alltid den lösning som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Lerum med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Lerums orter, från villatomterna vid Aspen och Sävelången till lägenheterna i centrum och husen i Gråbo, och vet att Hultets återvinningscentral ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och E20 går rakt genom Lerum. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon ort i Lerum, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Hultets återvinningscentral. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Lerum?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon ort i Lerum.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Lerum?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Lerum?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och E20 går rakt genom Lerum, så vi tar oss snabbt mellan orterna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Lerum?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Hämtar ni från villa, garage och sjöbod i Lerum?",
        answer:
          "Ja, de flesta av våra uppdrag i Lerum är villahämtningar i Lerum centrum, Floda, Stenkullen och vid sjöarna Aspen och Sävelången. Vi backar in på uppfarten där det går och tömmer garage, källare, vind, förråd och sjöbod. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Lerum?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Lerum centrum och Floda. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Lerum försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Lerum?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Lerum har en egen station, Hultets återvinningscentral, och avfallshämtningen sköts av Renova. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Lerum på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare, vind eller förråd i Lerum?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, vindsutrymmen, förråd, garage och sjöbodar. Vi är vana vid de rymliga villaförråden i Lerum och vid sommarvillorna runt sjöarna.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Lerum?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din villa i Floda och ett förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka orter i Lerum täcker ni?",
        answer:
          "Vi utför bortforsling i hela Lerum – Lerum (centrum, Aspedalen, Hulan, Hallsås), Floda, Stenkullen, Tollered, Gråbo, Hjällsnäs, Stamsjö, Sjövik och Björboholm. Inga extra avgifter för någon ort.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Lerum?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Lerumsbo på Hultets återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Lerum utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Lerum i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Lerum kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Lerum idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Lerums kommun.",
    },

    en: {
      slug: "junk-removal-lerum",
      meta: {
        title: "Junk Removal Lerum – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Lerum. We pick up furniture, appliances and bulky items in Lerum, Floda, Gråbo, Stenkullen, Sjövik and Tollered. Fixed price, often same day.",
        h1: "Junk Removal Lerum",
      },
      hero: {
        heading: "Junk Removal in Lerum",
        intro:
          "Book junk removal in Lerum – we collect furniture, appliances and bulky items across the whole municipality, from the villas in Lerum centre and by Lake Aspen to Floda, Gråbo, Stenkullen and Sjövik. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Lerum – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Lerum",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Lerum and driven to Hultets recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Floda, Lerum",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Lerum centre",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Gråbo, Lerum",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Stenkullen, Lerum",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Sjövik, Lerum",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Lerum – fast, hassle-free and responsible",
        mainText:
          "Lerum is a green municipality east of Gothenburg, known for its lakes Aspen and Sävelången, the Säveån river running through it, and one of Sweden's highest rates of home ownership. The towns of Lerum, Floda and Gråbo dominate, together with smaller communities like Stenkullen, Sjövik, Björboholm and Tollered. The housing is mostly villas and townhouses – from the old summer villas by the lakes to modern villa areas – with some apartments near the stations. That mix means junk removal in Lerum is often about clearing garages, basements, storage rooms and attics in villas, but sometimes also stairwells and elevators in apartment buildings. With Ungdomskraft, you skip the hassle entirely. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your villa, apartment or storage unit, load them into the van and drive to the nearest recycling centre. For Lerum, Hultets recycling centre is the municipality's own station, where bulky waste, e-waste and hazardous waste are received free of charge for residents. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E20 runs straight through Lerum and the Gothenburg–Alingsås commuter train stops at Aspen, Aspedalen, Lerum, Stenkullen and Floda, we move quickly between the towns, and we reach the inland town of Gråbo smoothly via Gråbovägen. This means we can often be on site the same day you book. Our workers are used to both the villa plots around Lake Aspen and the older summer villas by Lake Sävelången – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Lerum",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. Since most jobs in Lerum are villa pickups, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening, and whether the driveway is gravelled or steep down towards the lake. If you live in an apartment in Lerum centre or Floda, check whether there is a loading zone, courtyard or visitor parking near the entrance.\n\nGather the items in one place if possible, for example in the hallway, garage, storage room or by the boathouse. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Lerum resident you can drop those off for free at Hultets recycling centre.",
        additionalSections: [
          {
            heading: "Towns and areas we cover in Lerum",
            content:
              "We perform junk removal across the entire municipality of Lerum – from the central towns to the lakeside villa areas and the inland communities. Whether you live in a villa by Lake Aspen, a townhouse in Floda, an apartment in Lerum centre or a house in Gråbo, we can be on site, often the same day.\n\nIn Lerum – with the centre, Aspedalen, Hulan, Hallsås and Dergården – we are highly experienced with both villa pickups and apartment pickups near the commuter-rail station. In Floda and Stenkullen we meet villa housing along the Säveån and the cultural-heritage settings around Nääs castle and Tollered.\n\nWe are just as quick in the other parts of the municipality: Gråbo with Hjällsnäs and Stamsjö in the north, and Sjövik and Björboholm by Lake Sävelången. Thanks to the proximity of the E20, the commuter train and Gråbovägen, we always plan the route in advance so you don't have to wait. No extra charges for any town.",
          },
          {
            heading: "Recycling and environmental responsibility in Lerum",
            content:
              "An important part of our junk removal in Lerum is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Lerum has its own station, and waste collection in the municipality is handled by Renova:\n\nHultets recycling centre – Lerum's own recycling centre, which receives sorted bulky waste, e-waste, garden waste and hazardous waste, as well as items for reuse. Because Lerum cooperates with the other municipalities in the Gothenburg region, we can also drop off waste at nearby stations in Partille, Alingsås or Gothenburg when needed. We always choose the solution nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Lerum with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Lerum's towns, from the villa plots by Lake Aspen and Sävelången to the apartments in the centre and the houses in Gråbo, and that Hultets recycling centre is close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and the E20 runs straight through Lerum. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any town in Lerum, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Hultets recycling centre. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Lerum cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any town in Lerum.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Lerum?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Lerum?",
          answer:
            "We have vans and staff across the Gothenburg region and the E20 runs straight through Lerum, so we move quickly between the towns. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Lerum?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Do you collect from villas, garages and boathouses in Lerum?",
          answer:
            "Yes, most of our jobs in Lerum are villa pickups in Lerum centre, Floda, Stenkullen and by the lakes Aspen and Sävelången. We reverse onto the driveway where possible and clear garages, basements, attics, storage rooms and boathouses. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Lerum?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Lerum centre and Floda. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Lerum insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Lerum?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Lerum has its own station, Hultets recycling centre, and waste collection is handled by Renova. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Lerum on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements, attics or storage units in Lerum?",
          answer:
            "Yes, we pick up items from all spaces – basements, attics, storage rooms, garages and boathouses. We are used to the spacious villa storage rooms in Lerum and at the summer villas around the lakes.",
        },
        {
          question: "Can you make multiple stops during junk removal in Lerum?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your villa in Floda and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which towns in Lerum do you cover?",
          answer:
            "We perform junk removal across all of Lerum – Lerum (centre, Aspedalen, Hulan, Hallsås), Floda, Stenkullen, Tollered, Gråbo, Hjällsnäs, Stamsjö, Sjövik and Björboholm. No extra charges for any town.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Lerum?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Lerum resident you can drop those off for free at Hultets recycling centre.",
        },
        {
          question: "Which municipalities around Lerum do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Lerum in 3 easy steps",
        step2Text:
          "Our young workers in Lerum come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Lerum today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Lerum.",
      },
    },
  },
];
